import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Key, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useAuth } from '../../context/AuthContext';

export function TesterCodeModal({ isOpen, onClose }) {
  const navigate = useNavigate();
  const { isAuthenticated, authFetch, refreshUser } = useAuth();
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isAuthenticated) {
      setError('Please sign in or create a free account first, then redeem your code.');
      return;
    }

    if (!code.trim()) {
      setError('Please enter a tester code.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const resp = await authFetch('/api/tester-codes/claim', {
        method: 'POST',
        body: JSON.stringify({ code: code.trim() }),
      });
      const data = await resp.json();

      if (resp.ok && data.success) {
        setSuccess(true);
        await refreshUser();
        setTimeout(() => {
          onClose();
        }, 1800);
      } else {
        setError(data.detail || data.message || 'That code did not work.');
      }
    } catch (err) {
      console.error('Error validating code:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl fade-in">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
              <Key className="w-5 h-5 text-primary" />
            </div>
            <h2 className="font-heading font-bold text-xl text-text">Enter tester code</h2>
          </div>
          <button onClick={onClose} aria-label="Close" className="text-stone-400 hover:text-stone-600 transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        {success ? (
          <div className="text-center py-6">
            <div className="w-14 h-14 bg-success/10 rounded-full mx-auto mb-3 flex items-center justify-center">
              <CheckCircle className="w-7 h-7 text-success" />
            </div>
            <h3 className="font-heading font-bold text-lg text-text mb-2">Access granted</h3>
            <p className="text-text-muted">Enjoy the course!</p>
          </div>
        ) : !isAuthenticated ? (
          <div>
            <p className="text-text-muted mb-4">To redeem a tester code, please sign in or create a free account first. We tie codes to accounts so your access carries across devices.</p>
            <div className="flex gap-3">
              <Button onClick={() => { onClose(); navigate('/login'); }} className="flex-1 rounded-xl" data-testid="tester-signin">Sign in</Button>
              <Button variant="outline" onClick={() => { onClose(); navigate('/signup'); }} className="flex-1 rounded-xl" data-testid="tester-signup">Create account</Button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <label className="block text-sm font-semibold text-text mb-2">Tester code</label>
            <Input
              data-testid="tester-code-input"
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="BTA-XXXXXXXX"
              className="font-mono text-lg mb-3"
              autoFocus
            />
            {error && (
              <div className="flex items-start gap-2 text-error text-sm bg-error/10 p-3 rounded-xl mb-3">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}
            <Button type="submit" disabled={isLoading} data-testid="tester-code-submit" className="w-full rounded-xl py-5">
              {isLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Redeem code
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
